import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Users,
  TrendingUp,
  Globe,
  ArrowRight,
  Shield,
  Award,
  Target,
  Briefcase,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import Navbar from '../components/common/Navbar';
import Footer from '../components/common/Footer';
import FAQ from '../components/common/FAQ';

const NigerianDiasporaInvestors: React.FC = () => {
  const [isLearnMoreOpen, setIsLearnMoreOpen] = useState(false);

  // Update page title and meta description
  useEffect(() => {
    document.title = "Nigerian Diaspora Investment | Build Wealth & Invest in Nigeria's Future";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', "Join thousands of Nigerian diaspora investors worldwide contributing to Nigeria's economic growth. Explore secure, profitable, and transparent investment opportunities across projects in real estate, agriculture, tech, and more.");
    }
  }, []);

  const faqs = [
    {
      id: 1,
      question: "What is Nigerian diaspora investment?",
      answer: "Nigerian diaspora investment refers to opportunities designed for Nigerians living abroad who want to invest in projects that generate personal wealth while contributing to Nigeria's economic growth."
    },
    {
      id: 2,
      question: "Who can participate in diaspora investments?",
      answer: "Any Nigerian living overseas, whether in the US, UK, Europe, Asia, or beyond, can join and invest through trusted platforms."
    },
    {
      id: 3,
      question: "What types of projects can I invest in?",
      answer: "Investors can explore opportunities in real estate, agriculture, technology, renewable energy, and small business growth."
    },
    {
      id: 4,
      question: "How safe are these investments?",
      answer: "All projects are carefully vetted to ensure transparency, credibility, and long-term sustainability, giving investors confidence in their choices."
    },
    {
      id: 5,
      question: "How do I start investing as part of the Nigerian diaspora?",
      answer: "Simply sign up, explore available projects, and select the investment that aligns with your goals. The platform provides full online access to monitor progress and returns."
    }
  ];

  const regions = [
    {
      name: "United Kingdom",
      path: "/nigerian-diaspora-investors/uk",
      investors: "150,000+",
      investments: "$2.5B+",
      icon: "🇬🇧",
      description: "Connect with the thriving Nigerian community in the UK"
    },
    {
      name: "United States",
      path: "/nigerian-diaspora-investors/us",
      investors: "380,000+",
      investments: "$5.8B+",
      icon: "🇺🇸",
      description: "Join the largest Nigerian diaspora community in the US"
    },
    {
      name: "Europe",
      path: "/nigerian-diaspora-investors/europe",
      investors: "120,000+",
      investments: "$1.8B+",
      icon: "🇪🇺",
      description: "Explore opportunities with Nigerian professionals across Europe"
    },
    {
      name: "Asia",
      path: "/nigerian-diaspora-investors/asia",
      investors: "45,000+",
      investments: "$850M+",
      icon: "🌏",
      description: "Connect with the growing Nigerian community in Asia"
    }
  ];



  const benefits = [
    {
      icon: Shield,
      title: "Secure Investments",
      description: "All projects are thoroughly vetted and backed by our guarantee of security and transparency."
    },
    {
      icon: TrendingUp,
      title: "High Returns",
      description: "Access to exclusive investment opportunities with potential returns of 15-25% annually."
    },
    {
      icon: Globe,
      title: "Global Network",
      description: "Connect with a worldwide network of Nigerian professionals and successful investors."
    },
    {
      icon: Award,
      title: "Expert Guidance",
      description: "Get personalized investment advice from our team of financial and legal experts."
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Nigerian Diaspora Investors Page with Green Color Scheme */}
      <Navbar />

      {/* Simple Hero Section */}
      <section className="relative py-12 px-6 bg-white border-b border-gray-200">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-gray-50 px-4 py-2 rounded-full text-sm font-medium mb-6 text-gray-600 border border-gray-200">
              <Users className="w-4 h-4" />
              Nigerian Diaspora Investment
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
              Nigerian Diaspora Investors – Invest in Nigeria's Future
            </h1>
            <p className="text-lg text-gray-600 max-w-4xl mx-auto mb-8">
              Join thousands of Nigerian professionals across the globe who are building wealth while supporting Nigeria's economic development through trusted investment opportunities.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://app.diasporawise.com"
                className="bg-gray-800 text-white px-8 py-4 rounded-lg font-semibold hover:bg-gray-900 transition-all duration-200 flex items-center justify-center gap-2 lg:hidden"
              >
                <Briefcase className="w-5 h-5" />
                Start Investing Today
              </a>
              <button
                onClick={() => {
                  setIsLearnMoreOpen(true);
                  setTimeout(() => {
                    document.getElementById('nigerian-diaspora-content')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }, 100);
                }}
                className="bg-white border border-gray-300 text-gray-700 px-8 py-4 rounded-lg font-semibold hover:bg-gray-50 transition-all duration-200 flex items-center justify-center gap-2"
              >
                <Globe className="w-5 h-5" />
                Learn More About Diaspora Investments
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Regional Communities */}
      <section className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Join Your Regional Community
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Connect with Nigerian professionals in your region and access region-specific investment opportunities and support.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {regions.map((region, index) => (
              <Link
                key={index}
                to={region.path}
                className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden border border-gray-100"
              >
                <div className="p-8 text-center">
                  <div className="text-4xl mb-4">{region.icon}</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-green-600 transition-colors duration-300">
                    {region.name}
                  </h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    {region.description}
                  </p>

                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="text-center">
                      <p className="text-lg font-bold text-gray-900">{region.investors}</p>
                      <p className="text-xs text-gray-500">Investors</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-bold text-green-600">{region.investments}</p>
                      <p className="text-xs text-gray-500">Invested</p>
                    </div>
                  </div>

                  <div className="flex items-center justify-center gap-2 text-green-600 font-semibold group-hover:gap-3 transition-all duration-300">
                    <span>Explore Community</span>
                    <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose DiasporaWise?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We provide the tools, resources, and support you need to make successful investments in Nigeria.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center">
                <div className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 p-8 border border-gray-100">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <benefit.icon className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{benefit.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Investment Process */}
      <section className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Simple Investment Process
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Get started with investing in Nigeria in just a few simple steps.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              {
                step: "01",
                title: "Register & Verify",
                description: "Create your account and complete the verification process to access investment opportunities."
              },
              {
                step: "02",
                title: "Browse Projects",
                description: "Explore our curated list of vetted investment opportunities across various sectors."
              },
              {
                step: "03",
                title: "Invest Securely",
                description: "Make your investment through our secure platform with full legal protection."
              },
              {
                step: "04",
                title: "Track & Grow",
                description: "Monitor your investments and watch your wealth grow with regular updates and reports."
              }
            ].map((step, index) => (
              <div key={index} className="text-center">
                <div className="relative">
                  <div className="w-16 h-16 bg-green-600 text-white rounded-full flex items-center justify-center mx-auto mb-6 text-xl font-bold">
                    {step.step}
                  </div>
                  {index < 3 && (
                    <div className="hidden md:block absolute top-8 left-1/2 w-full h-0.5 bg-gray-200 -z-10"></div>
                  )}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{step.title}</h3>
                <p className="text-gray-600 leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-r from-green-600 to-green-700 text-white">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Start Your Investment Journey?
          </h2>
          <p className="text-xl text-green-100 mb-8">
            Join thousands of successful Nigerian diaspora investors who are building wealth and contributing to Nigeria's growth.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://app.diasporawise.com"
              className="bg-white text-green-700 px-8 py-4 rounded-xl font-semibold hover:bg-green-50 transition-all duration-300 transform hover:-translate-y-1 hover:shadow-lg flex items-center justify-center gap-2 lg:hidden"
            >
              <Target className="w-5 h-5" />
              Get Started Now
            </a>
            <Link
              to="/contact"
              className="bg-white/10 backdrop-blur-sm border border-white/20 text-white px-8 py-4 rounded-xl font-semibold hover:bg-white/20 transition-all duration-300 flex items-center justify-center gap-2"
            >
              <Users className="w-5 h-5" />
              Speak to an Expert
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQ
        title="Frequently Asked Questions"
        subtitle="Everything you need to know about Nigerian diaspora investment opportunities"
        faqs={faqs}
      />

      {/* Learn More About Nigerian Diaspora Investment Section */}
      <section className="bg-white py-12 px-4 border-t border-gray-200">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <button
              onClick={() => setIsLearnMoreOpen(!isLearnMoreOpen)}
              className="flex items-center justify-center gap-2 mx-auto bg-gray-100 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-200 transition-colors duration-300 border border-gray-300"
              aria-expanded={isLearnMoreOpen}
              aria-controls="nigerian-diaspora-content"
            >
              <span className="font-medium">
                {isLearnMoreOpen ? 'Hide Learn More About Nigerian Diaspora Investment' : 'Learn More About Nigerian Diaspora Investment'}
              </span>
              {isLearnMoreOpen ? (
                <ChevronUp className="w-4 h-4 text-gray-600" />
              ) : (
                <ChevronDown className="w-4 h-4 text-gray-600" />
              )}
            </button>
          </div>

          <div
            id="nigerian-diaspora-content"
            className={`overflow-hidden transition-all duration-500 ease-in-out ${isLearnMoreOpen ? 'max-h-none opacity-100' : 'max-h-0 opacity-0'
              }`}
          >
            <article className="prose prose-lg max-w-none">
              {/* Main Content Section */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  Nigerian Diaspora Investment: How to Build Wealth and Invest in Nigeria's Future
                </h2>
              </div>

              {/* Section 1 */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">1. Introduction: Why Nigerian Diaspora Investment Matters</h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Nigerians living overseas send billions of dollars home each year in remittances. These funds have been crucial for families, but they do not typically lead to the creation of long-lasting wealth or development. That is why Nigerian diaspora investment is more important than ever.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  If Nigerians abroad move from one-off remittances to a more structured form of investment, they can help secure their own financial future while also contributing to Nigeria's growth. Whether you reside in the US, UK, Europe, or Asia, you can now invest safely in Nigeria from abroad.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  The objective is straightforward: enable the Nigerian diaspora community to build wealth, create jobs, and generate a positive economic impact in Nigeria through secure and transparent investment opportunities.
                </p>
              </div>

              {/* Section 2 */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">2. Understanding Nigerian Diaspora Investments</h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  A Nigerian diaspora investment is an opportunity designed for Nigerians living abroad to invest in their home country. Unlike simple money transfers, which usually go toward family essentials, diaspora investments are long-term wealth-building strategies.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  The Nigerian diaspora community has become instrumental in driving participation in sectors such as real estate, agriculture, technology, and renewable energy. Today, the process is far more transparent, thanks to diaspora investment platforms that connect Nigerians abroad to vetted projects.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  In summary, investing through these platforms gives you the chance to invest in Nigeria securely, grow your money, and have a meaningful impact on your home country.
                </p>
              </div>

              {/* Section 3 */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">3. Best Sectors for Diaspora Investment in Nigeria</h2>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Real Estate Development</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Real estate investment in Nigeria remains one of the most reliable ways to build wealth. With the increasing demand for affordable housing and modern commercial buildings, Nigerian diaspora investors can participate in estates, office complexes, and rental apartments that generate steady income and increase in value over time.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Agriculture and Agribusiness</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Nigeria's agricultural sector offers significant potential. From crop farming to food processing, agriculture investment in Nigeria creates jobs and ensures food security while delivering attractive returns. For diaspora investors, it's an opportunity that combines profit with national impact.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Technology and Startups</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Nigeria's tech ecosystem is one of the fastest-growing in Africa. From fintech innovations to health tech solutions, technology investment in Nigeria is booming. Nigerian professionals abroad can support this innovation and benefit from high-growth opportunities while strengthening the digital economy.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Renewable Energy</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Nigeria's power challenges create strong opportunities for renewable energy investment in Nigeria. Projects in solar, wind, and clean energy are actively seeking funding. For diaspora investors, this sector combines profitability with social impact by bringing electricity to millions of homes.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">SMEs and Infrastructure</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Small and medium enterprises are the backbone of Nigeria's economy. By supporting SME and infrastructure investment in Nigeria, the diaspora can help create jobs, enhance transport, and boost local industries. This is one of the strongest ways to directly contribute to Nigeria's economic growth.
                </p>
              </div>

              {/* Section 4 */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">4. Benefits of Investing in Nigeria as a Diaspora</h2>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Create Wealth</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Unlike remittances, diaspora investments help you build lasting financial assets connected to solid projects.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Support Nation Building</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Every dollar invested supports jobs, housing, energy, and development. By choosing transparent investments in Nigeria, you contribute to nation building.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Stay Connected to Nigeria</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Living abroad can sometimes weaken ties to home. By becoming a Nigerian diaspora investor, you remain connected while making an impact.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Safe and Transparent Opportunities</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Regulated platforms provide secure investments in Nigeria managed with professionalism and integrity.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Generational Benefits</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Investments made today offer financial security for your family across generations. Diaspora investments are truly a gift to the future.
                </p>
              </div>

              {/* Section 5 */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">5. How to Start Investing from Abroad</h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Modern technology and regulated platforms now make it easier for Nigerian professionals abroad to invest safely from anywhere. Below are regional insights tailored for Nigerians across the globe:
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Nigerian Diaspora Investment in the USA</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  For Nigerians living in the United States, investing back home is more accessible than ever. Secure digital platforms allow you to invest in Nigeria from abroad without physical presence. Many diaspora professionals in the US focus on real estate investment in Nigeria and technology investment in Nigeria because they combine growth with stability. The US financial system also makes cross-border transactions seamless.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Nigerian Diaspora Investment in the UK</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  The UK hosts one of the largest Nigerian diaspora communities. Many are already active diaspora investors in Nigeria, particularly in agriculture investment and renewable energy projects, which combine impact with reliable returns. Secure platforms have further reduced risks and allowed UK-based Nigerians to invest with greater transparency.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Nigerian Diaspora Investment in Europe</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Across Europe—from Germany to France and beyond—Nigerians are increasingly participating in SMEs and infrastructure investment in Nigeria. These opportunities allow the diaspora to grow wealth while also contributing to Nigeria's economic growth. Organized platforms have made it easier to channel investments safely.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Nigerian Diaspora Investment in Asia</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  In Asia, particularly in countries such as China, the UAE, and Malaysia, Nigerians are showing interest in renewable energy and technology startups. These sectors align with Asia's innovation-driven markets. For Nigerian professionals abroad in Asia, these opportunities present both strong returns and a meaningful way to remain connected to home.
                </p>
              </div>

              {/* Section 6 */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">6. Future of Nigerian Diaspora Investments</h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  The future of diaspora investment in Nigeria is promising, driven by technology, policy, and new business models.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Fintech Growth</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  New payment solutions allow Nigerians abroad to invest in Nigeria from anywhere with just a few clicks.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Green Projects</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Renewable energy investment in Nigeria will continue to be a cornerstone of sustainable development.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Government Incentives</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Nigeria's government is actively encouraging the diaspora to take part in secure and profitable investments.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Regional Networks</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Platforms dedicated to Nigerians in the US, UK, Europe, and Asia are expanding, offering tailored pipelines for investors abroad.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  In the next decade, Nigerian diaspora investors will not just send money home—they will be a powerful force shaping Nigeria's economic growth.
                </p>
              </div>

              {/* Section 7 - Conclusion */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">7. Conclusion: Wealth Creation and Nation Building</h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  The story of Nigerian diaspora investment goes far beyond money. It is about creating wealth, supporting families, and contributing to Nigeria's future. Whether through real estate, agriculture, technology, renewable energy, or SMEs, there are safe and profitable opportunities for Nigerians abroad.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  With regulated diaspora investment platforms, you can now invest in Nigeria from abroad securely. This is your opportunity to build wealth, give back, and stay connected to your roots.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Start your diaspora investment journey today and join a growing global community committed to building a stronger Nigeria.
                </p>
              </div>
            </article>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default NigerianDiasporaInvestors;
